import { AppDispatch } from '@/app/store';
import { setTheme, markThemeInitialized } from '@/app/store/slices/themeSlice';
import { fetchUserThemePreferences } from '@/app/store/thunks/themeThunks';
import { 
  detectSystemTheme, 
  loadGuestThemePreference, 
  updateHtmlAttributes,
  clearGuestThemePreference,
  saveGuestThemePreference
} from './simpleThemeUtils';
import { ColorTheme } from '@/app/store/slices/themeSlice';

/**
 * Initialize theme on app load
 * Optimized approach: Minimize visual transitions during login
 */
export const initializeSimpleTheme = async (
  dispatch: AppDispatch,
  userId?: number,
  isAuthenticated?: boolean
): Promise<void> => {
  try {
    console.log('Initializing theme...', { userId, isAuthenticated });

    // For authenticated users, try to get user preferences first to avoid double transition
    if (isAuthenticated && userId) {
      console.log('Authenticated user - fetching preferences directly to avoid double transition');
      
      try {
        // Fetch user preferences directly without applying localStorage theme first
        await dispatch(fetchUserThemePreferences(userId));
        
        console.log('User theme preferences applied successfully');
        
        // Clear guest preference since user is authenticated
        clearGuestThemePreference();
        
        // Mark as initialized and exit early
        dispatch(markThemeInitialized());
        return;
        
      } catch (error) {
        console.error('Failed to fetch user theme preferences, falling back to localStorage:', error);
        // Continue to localStorage fallback below
      }
    }

    // Fallback: Load localStorage theme (for guests or if user fetch failed)
    const guestTheme = loadGuestThemePreference();
    if (guestTheme) {
      console.log('Loading theme from localStorage:', guestTheme);
      dispatch(setTheme({ mode: guestTheme.mode, colorTheme: guestTheme.colorTheme }));
      
      // Wait for Redux to settle
      await new Promise(resolve => setTimeout(resolve, 50));
      
      updateHtmlAttributes(guestTheme.mode, guestTheme.colorTheme);
      
      // Wait for DOM update to complete
      await new Promise(resolve => setTimeout(resolve, 50));
    } else {
      // New user - detect system theme
      const systemTheme = detectSystemTheme();
      const defaultColorTheme: ColorTheme = 'purple';
      
      console.log('New user, using system theme:', systemTheme);
      
      dispatch(setTheme({ mode: systemTheme, colorTheme: defaultColorTheme }));
      
      // Wait for Redux to settle
      await new Promise(resolve => setTimeout(resolve, 50));
      
      updateHtmlAttributes(systemTheme, defaultColorTheme);
      
      // Save for future visits
      if (!isAuthenticated) {
        saveGuestThemePreference(systemTheme, defaultColorTheme);
      }
    }
    
    // Mark as initialized
    dispatch(markThemeInitialized());
    
  } catch (error) {
    console.error('Theme initialization failed:', error);
    
    // Fallback to defaults
    dispatch(setTheme({ mode: 'system', colorTheme: 'purple' }));
    updateHtmlAttributes('system', 'purple');
    dispatch(markThemeInitialized());
  }
};

/**
 * Migrate guest theme preference to database after login
 * Called after successful authentication
 */
export const migrateGuestThemePreference = async (
  dispatch: AppDispatch,
  userId: number
): Promise<void> => {
  try {
    const guestTheme = loadGuestThemePreference();
    
    if (guestTheme) {
      console.log('Migrating guest theme preferences to database:', guestTheme);
      
      // Import the update thunk
      const { updateUserThemePreferences } = await import('@/app/store/thunks/themeThunks');
      
      // Update in database (partitionKey defaults to PARTITION_KEY)
      await dispatch(updateUserThemePreferences(userId, guestTheme.mode, guestTheme.colorTheme));
      
      // Clear local storage after successful migration
      clearGuestThemePreference();
      
      console.log('Guest theme preferences migrated successfully');
    }
  } catch (error) {
    console.error('Failed to migrate guest theme preferences:', error);
    // Don't throw - this is not critical
  }
};